/**
 * Cognito User Pool client for the SPA — direct SDK calls, no Hosted UI.
 *
 * We use `amazon-cognito-identity-js` (the AWS-recommended browser SDK)
 * with USER_SRP_AUTH for sign-in. Password never crosses the wire — SRP
 * derives a shared key client+server. The SDK handles the BigInt math
 * and round-trips so we don't reimplement it.
 *
 * Tokens live in localStorage so they survive tab reloads. Trade-off:
 * accessible to any JS on this origin (XSS risk). Mitigated by us
 * controlling 100% of the SPA bundle (no third-party scripts).
 *
 * Tokens expire after 60 min (Cognito User Pool Client setting). On
 * 401, the caller redirects to /signin and the user re-authenticates
 * with email + password.
 *
 * Public sign-up is gated behind a PostConfirmation Lambda that
 * disables every newly-confirmed user (waitlist). See
 * infra/lambdas/post-confirmation/.
 */

import {
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession,
  AuthenticationDetails,
  ISignUpResult,
} from "amazon-cognito-identity-js";

import { getConfig } from "./env";

const TOKENS_KEY = "sm.auth.tokens";
const PENDING_EMAIL_KEY = "sm.auth.pending_email";
const PKCE_VERIFIER_KEY = "sm.auth.pkce_verifier";
const PKCE_STATE_KEY = "sm.auth.pkce_state";
const PKCE_NEXT_KEY = "sm.auth.pkce_next";
const PKCE_REDIRECT_KEY = "sm.auth.pkce_redirect";

export interface Tokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  expiresAt: number; // epoch ms
}

export interface AuthUser {
  sub: string;
  email: string | null;
  emailVerified: boolean;
  groups: string[];
  isAdmin: boolean;
}

const ADMIN_GROUP = "sm-admins";

// We keep one CognitoUserPool per backing Storage. The SDK writes its
// own `CognitoIdentityServiceProvider.<clientId>.{idToken,...}` keys
// into whichever Storage the pool was built with — so to honor
// "Keep me signed in = off" we need a pool bound to sessionStorage,
// not localStorage. Without that the SDK's tokens survive across
// browser restarts even when the user said "this tab only".
let _poolLocal: CognitoUserPool | null = null;
let _poolSession: CognitoUserPool | null = null;

function buildPool(storage: Storage): CognitoUserPool {
  const cfg = getConfig();
  if (!cfg.cognitoClientId) {
    throw new Error("Cognito config missing — check /config.js loaded");
  }
  if (!cfg.cognitoUserPoolId) {
    throw new Error(
      "Cognito user pool ID missing from runtime config — redeploy to refresh /config.js",
    );
  }
  return new CognitoUserPool({
    UserPoolId: cfg.cognitoUserPoolId,
    ClientId: cfg.cognitoClientId,
    Storage: storage,
  });
}

function pool(storage: Storage = storageInUse()): CognitoUserPool {
  if (storage === window.sessionStorage) {
    if (!_poolSession) _poolSession = buildPool(window.sessionStorage);
    return _poolSession;
  }
  if (!_poolLocal) _poolLocal = buildPool(window.localStorage);
  return _poolLocal;
}

/**
 * Pick the Storage the user previously authenticated against, by
 * sniffing where our token marker lives. New sign-ins pass an
 * explicit storage; everywhere else (refresh, signOut, getCurrentUser)
 * falls back to whatever the previous sign-in chose.
 */
function storageInUse(): Storage {
  if (window.sessionStorage.getItem(TOKENS_KEY)) return window.sessionStorage;
  return window.localStorage;
}

// ─── Token storage ────────────────────────────────────────────────────

export function loadTokens(): Tokens | null {
  // Prefer localStorage (persisted across reloads). Fall back to
  // sessionStorage so "this-tab-only" sign-ins still hydrate the
  // AuthContext on the same tab.
  for (const store of [window.localStorage, window.sessionStorage]) {
    try {
      const raw = store.getItem(TOKENS_KEY);
      if (!raw) continue;
      const parsed = JSON.parse(raw) as Tokens;
      if (parsed.idToken && parsed.accessToken && parsed.expiresAt) return parsed;
    } catch {
      // ignore parse / DOMException and try the next store
    }
  }
  return null;
}

function saveTokens(t: Tokens, remember: boolean): void {
  const store = remember ? window.localStorage : window.sessionStorage;
  const other = remember ? window.sessionStorage : window.localStorage;
  store.setItem(TOKENS_KEY, JSON.stringify(t));
  // Make sure we don't keep a stale copy in the other store.
  other.removeItem(TOKENS_KEY);
}

function clearTokens(): void {
  window.localStorage.removeItem(TOKENS_KEY);
  window.sessionStorage.removeItem(TOKENS_KEY);
}

export function tokensExpired(t: Tokens): boolean {
  // 30s skew so in-flight requests don't fail with a freshly-issued
  // token that's about to expire mid-request.
  return Date.now() >= t.expiresAt - 30_000;
}

function tokensFromSession(s: CognitoUserSession): Tokens {
  const id = s.getIdToken();
  return {
    idToken: id.getJwtToken(),
    accessToken: s.getAccessToken().getJwtToken(),
    refreshToken: s.getRefreshToken().getToken(),
    expiresAt: id.getExpiration() * 1000,
  };
}

// ─── ID token decoding ────────────────────────────────────────────────

function decodeJwtPayload(jwt: string): Record<string, unknown> | null {
  try {
    const parts = jwt.split(".");
    if (parts.length !== 3) return null;
    const payload = parts[1];
    if (!payload) return null;
    const padded = payload.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function extractGroups(claims: Record<string, unknown>): string[] {
  const raw = claims["cognito:groups"];
  if (Array.isArray(raw)) return raw.filter((g): g is string => typeof g === "string");
  if (typeof raw === "string") return [raw];
  return [];
}

export function userFromTokens(t: Tokens): AuthUser | null {
  const claims = decodeJwtPayload(t.idToken);
  if (!claims || typeof claims.sub !== "string") return null;
  const groups = extractGroups(claims);
  return {
    sub: claims.sub,
    email: typeof claims.email === "string" ? claims.email : null,
    emailVerified: claims.email_verified === true,
    groups,
    isAdmin: groups.includes(ADMIN_GROUP),
  };
}

// ─── Error taxonomy ───────────────────────────────────────────────────
// PreventUserExistenceErrors=ENABLED on the User Pool Client collapses
// "wrong password", "no such user", and "user unconfirmed" into the same
// NotAuthorizedException — by design, to prevent account enumeration.
// We mirror that by mapping them all to one safe message.

export type AuthErrorKind =
  | "invalid_credentials"
  | "waitlist_pending"
  | "user_not_confirmed"
  | "user_exists"
  | "code_mismatch"
  | "code_expired"
  | "limit_exceeded"
  | "weak_password"
  | "unknown";

export interface AuthError extends Error {
  kind: AuthErrorKind;
}

function makeAuthError(kind: AuthErrorKind, message: string): AuthError {
  const err = new Error(message) as AuthError;
  err.kind = kind;
  return err;
}

function classify(err: unknown): AuthError {
  const msg = (err instanceof Error ? err.message : String(err)) || "";
  const code = ((err as { code?: string; name?: string }).code
    ?? (err as { code?: string; name?: string }).name
    ?? "") as string;

  if (/User is disabled/i.test(msg)) {
    return makeAuthError("waitlist_pending", "Your account is on the waitlist.");
  }
  if (code === "UserNotConfirmedException" || /not confirmed/i.test(msg)) {
    return makeAuthError(
      "user_not_confirmed",
      "Confirm your email first — we sent you a 6-digit code.",
    );
  }
  if (code === "UsernameExistsException" || /already exists/i.test(msg)) {
    return makeAuthError(
      "user_exists",
      "That email is already signed up. Try signing in, or enter your verification code if you haven't yet.",
    );
  }
  if (code === "CodeMismatchException" || /Invalid verification code/i.test(msg)) {
    return makeAuthError("code_mismatch", "That code doesn't match. Try again.");
  }
  if (code === "ExpiredCodeException" || /expired/i.test(msg)) {
    return makeAuthError("code_expired", "That code has expired. Request a new one.");
  }
  if (code === "LimitExceededException" || /Attempt limit/i.test(msg)) {
    return makeAuthError(
      "limit_exceeded",
      "Too many attempts. Wait a few minutes and try again.",
    );
  }
  if (code === "InvalidPasswordException" || /password did not conform/i.test(msg)) {
    return makeAuthError(
      "weak_password",
      "Password needs at least 12 characters with upper, lower, and a number.",
    );
  }
  if (code === "NotAuthorizedException" || /Incorrect username or password/i.test(msg)) {
    return makeAuthError("invalid_credentials", "Check your email and password.");
  }
  return makeAuthError("unknown", msg || "Something went wrong. Try again.");
}

// ─── Sign-in ──────────────────────────────────────────────────────────

/**
 * Result of a sign-in attempt. The happy path returns tokens directly; if
 * Cognito challenges the user for their software-token MFA code, we
 * return a `totp` variant that exposes a `submit(code)` closure capturing
 * the same `CognitoUser` instance. SignIn.tsx renders the TOTP screen
 * and calls `submit` to complete sign-in.
 */
export type SignInResult =
  | { kind: "ok"; tokens: Tokens }
  | { kind: "totp"; submit: (code: string) => Promise<Tokens> };

export async function signInWithPassword(
  email: string,
  password: string,
  remember = true,
): Promise<SignInResult> {
  const storage = remember ? window.localStorage : window.sessionStorage;
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({
      Username: email,
      Pool: pool(storage),
      Storage: storage,
    });
    const details = new AuthenticationDetails({ Username: email, Password: password });
    user.authenticateUser(details, {
      onSuccess: (session) => {
        const t = tokensFromSession(session);
        saveTokens(t, remember);
        resolve({ kind: "ok", tokens: t });
      },
      onFailure: (err) => reject(classify(err)),
      newPasswordRequired: () =>
        reject(makeAuthError("unknown", "Password reset required — contact support.")),
      totpRequired: () => {
        const submit = (code: string): Promise<Tokens> =>
          new Promise<Tokens>((res, rej) => {
            user.sendMFACode(
              code,
              {
                onSuccess: (session) => {
                  const t = tokensFromSession(session);
                  saveTokens(t, remember);
                  res(t);
                },
                onFailure: (err) => rej(classify(err)),
              },
              "SOFTWARE_TOKEN_MFA",
            );
          });
        resolve({ kind: "totp", submit });
      },
    });
  });
}

// ─── Sign-up ──────────────────────────────────────────────────────────

export interface SignUpInput {
  email: string;
  password: string;
  workspaceName: string;
}

export interface SignUpResult {
  email: string;
  needsVerification: boolean;
}

export async function signUp(input: SignUpInput): Promise<SignUpResult> {
  const attrs = [
    new CognitoUserAttribute({ Name: "email", Value: input.email }),
    new CognitoUserAttribute({
      Name: "custom:initialWorkspaceName",
      Value: input.workspaceName,
    }),
  ];
  return new Promise((resolve, reject) => {
    pool().signUp(input.email, input.password, attrs, [], (err, result) => {
      if (err) return reject(classify(err));
      const r = result as ISignUpResult | undefined;
      window.sessionStorage.setItem(PENDING_EMAIL_KEY, input.email);
      resolve({
        email: input.email,
        needsVerification: r ? !r.userConfirmed : true,
      });
    });
  });
}

export async function confirmSignUp(email: string, code: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: pool() });
    user.confirmRegistration(code, true, (err) => {
      if (err) return reject(classify(err));
      window.sessionStorage.removeItem(PENDING_EMAIL_KEY);
      resolve();
    });
  });
}

export async function resendConfirmationCode(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: pool() });
    user.resendConfirmationCode((err) => {
      if (err) return reject(classify(err));
      resolve();
    });
  });
}

export function pendingSignUpEmail(): string | null {
  return window.sessionStorage.getItem(PENDING_EMAIL_KEY);
}

// ─── Password reset ───────────────────────────────────────────────────

export async function forgotPassword(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: pool() });
    user.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(classify(err)),
    });
  });
}

export async function confirmPasswordReset(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: pool() });
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(classify(err)),
    });
  });
}

// ─── Google sign-in (Cognito Hosted UI + PKCE) ───────────────────────

/**
 * Whether Google sign-in is available right now — driven by whether
 * deploy.yml has populated `cognitoDomain` in runtime config (which it
 * only does once the GoogleClientId SSM parameter is set + the Cognito
 * Hosted UI domain comes online). Callers use this to decide whether to
 * render the "Continue with Google" button.
 */
export function googleSignInAvailable(): boolean {
  return Boolean(getConfig().cognitoDomain);
}

/** Build the per-stage callback URL the SPA hands to Cognito. */
function callbackUrl(): string {
  return `${window.location.origin}/auth/callback`;
}

/** Random base64url string of `bytes` random bytes (crypto.getRandomValues). */
function randomBase64Url(bytes: number): string {
  const buf = new Uint8Array(bytes);
  window.crypto.getRandomValues(buf);
  return base64UrlEncode(buf);
}

function base64UrlEncode(buf: Uint8Array): string {
  let s = "";
  for (const b of buf) s += String.fromCharCode(b);
  return window
    .btoa(s)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

/** SHA-256 the verifier → base64url. The "S256" PKCE code_challenge. */
async function pkceChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const hash = await window.crypto.subtle.digest("SHA-256", data);
  return base64UrlEncode(new Uint8Array(hash));
}

/**
 * Wipe every PKCE key from sessionStorage. Exported so the callback
 * route can clear state on its error paths — exchangeGoogleCallback's
 * `finally` only fires when we actually attempted the token POST.
 */
export function clearPkceState(): void {
  window.sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  window.sessionStorage.removeItem(PKCE_STATE_KEY);
  window.sessionStorage.removeItem(PKCE_NEXT_KEY);
  window.sessionStorage.removeItem(PKCE_REDIRECT_KEY);
}

/**
 * Begin a Google sign-in via Cognito's Hosted UI. Stashes the PKCE
 * verifier + state + post-signin redirect target in sessionStorage so
 * the callback page can complete the exchange in the same tab.
 *
 * Throws if `cognitoDomain` isn't set in runtime config (the button
 * should be hidden in that case; this is a defense against misuse).
 */
/**
 * Generic Hosted UI sign-in starter. Builds + stashes the PKCE
 * verifier / state / next / redirect_uri tuple, then navigates to
 * `/oauth2/authorize?identity_provider=<providerName>`. Used by both
 * Google (`providerName = "Google"`) and per-workspace SAML
 * (`providerName = "SAML-<wsId>"`) — the exchange path at
 * `/auth/callback` doesn't need to know which IdP minted the code.
 */
async function startHostedUiSignIn(
  providerName: string,
  next: string,
): Promise<void> {
  const cfg = getConfig();
  if (!cfg.cognitoDomain) {
    throw new Error(
      "Hosted UI sign-in isn't configured — cognitoDomain missing from runtime config.",
    );
  }

  const verifier = randomBase64Url(32);
  const state = randomBase64Url(16);
  const challenge = await pkceChallenge(verifier);
  const redirectUri = callbackUrl();

  window.sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  window.sessionStorage.setItem(PKCE_STATE_KEY, state);
  window.sessionStorage.setItem(PKCE_NEXT_KEY, next);
  window.sessionStorage.setItem(PKCE_REDIRECT_KEY, redirectUri);

  const params = new URLSearchParams({
    identity_provider: providerName,
    response_type: "code",
    client_id: cfg.cognitoClientId,
    redirect_uri: redirectUri,
    scope: "openid email profile",
    state,
    code_challenge: challenge,
    code_challenge_method: "S256",
  });

  window.location.assign(`https://${cfg.cognitoDomain}/oauth2/authorize?${params}`);
}

export async function startGoogleSignIn(next = "/app"): Promise<void> {
  return startHostedUiSignIn("Google", next);
}

/**
 * Start a per-workspace SAML sign-in. The provider name comes from the
 * public lookup endpoint (`/api/auth/workspace/<wsId>/saml`) so the
 * SPA doesn't have to hard-code the `SAML-<wsId>` naming convention.
 */
export async function startSamlSignIn(
  providerName: string,
  next = "/app",
): Promise<void> {
  return startHostedUiSignIn(providerName, next);
}

/**
 * Where the SPA should land after a successful callback. Doesn't clear
 * the key — every callback exit path calls `clearPkceState()` separately,
 * and the value needs to survive being read on error branches.
 */
export function readPkceNext(): string {
  return window.sessionStorage.getItem(PKCE_NEXT_KEY) ?? "/app";
}

/**
 * Complete the OAuth code exchange. Validates `state` against what was
 * stashed, POSTs the code to Cognito's `/oauth2/token` with the PKCE
 * verifier (no client_secret — the SPA is a public client), parses the
 * response into `Tokens`, saves them, and clears the PKCE state.
 *
 * Caller is expected to have already short-circuited on `?error` in the
 * callback URL — this function assumes a `code` is present.
 */
export async function exchangeGoogleCallback(
  code: string,
  state: string,
): Promise<Tokens> {
  const cfg = getConfig();
  if (!cfg.cognitoDomain) {
    throw new Error("cognitoDomain missing — can't complete Google sign-in.");
  }

  const expectedState = window.sessionStorage.getItem(PKCE_STATE_KEY);
  const verifier = window.sessionStorage.getItem(PKCE_VERIFIER_KEY);
  const redirectUri = window.sessionStorage.getItem(PKCE_REDIRECT_KEY);

  if (!expectedState || !verifier || !redirectUri) {
    clearPkceState();
    throw new Error("Google sign-in state was missing or expired. Try again.");
  }
  if (state !== expectedState) {
    clearPkceState();
    throw new Error("Google sign-in state didn't match (possible CSRF). Try again.");
  }

  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: cfg.cognitoClientId,
    code,
    redirect_uri: redirectUri,
    code_verifier: verifier,
  });

  let res: Response;
  try {
    res = await fetch(`https://${cfg.cognitoDomain}/oauth2/token`, {
      method: "POST",
      headers: { "content-type": "application/x-www-form-urlencoded" },
      body,
    });
  } finally {
    // Always clear PKCE — the verifier is one-shot.
    clearPkceState();
  }

  if (!res.ok) {
    let detail = `${res.status}`;
    try {
      const j = (await res.json()) as { error_description?: string; error?: string };
      detail = j.error_description ?? j.error ?? detail;
    } catch {
      // ignore
    }
    // Cognito surfaces the waitlist gate as a token-endpoint failure for
    // first-time federated users: PostConfirmation calls AdminDisableUser
    // before the SPA POSTs /oauth2/token, so the exchange 400s with a
    // "User is disabled" / "invalid_grant" body. Map that to the typed
    // waitlist error so AuthCallback routes the user to the waitlist
    // banner instead of a generic "Sign-in failed" message.
    if (/disab/i.test(detail)) {
      throw makeAuthError("waitlist_pending", detail);
    }
    throw makeAuthError("unknown", `Google sign-in failed: ${detail}`);
  }

  const json = (await res.json()) as {
    id_token?: string;
    access_token?: string;
    refresh_token?: string;
    expires_in?: number;
  };
  if (!json.id_token || !json.access_token || !json.refresh_token || !json.expires_in) {
    throw new Error("Cognito token response missing fields.");
  }

  const tokens: Tokens = {
    idToken: json.id_token,
    accessToken: json.access_token,
    refreshToken: json.refresh_token,
    expiresAt: Date.now() + json.expires_in * 1000,
  };
  // Federated sessions use localStorage (persist across reloads) — there's
  // no "remember me" checkbox in the Hosted UI flow.
  saveTokens(tokens, true);
  return tokens;
}

// ─── Sign-out ─────────────────────────────────────────────────────────

/**
 * Clear local tokens and invalidate the refresh token globally.
 *
 * Two paths, run in parallel because they invalidate different things:
 *
 *   1. /oauth2/revoke (REST) — invalidates the refresh token directly.
 *      Works for federated sessions, which the SDK pool doesn't know
 *      about (`exchangeGoogleCallback` writes only our token blob, not
 *      the SDK's `CognitoIdentityServiceProvider.*` keys). Without
 *      this, a Google-authed user's refresh token stayed live for its
 *      full 30-day TTL after sign-out.
 *
 *   2. SDK `globalSignOut` — invalidates ALL sessions for the user
 *      (every device). Only fires for native sign-ins because the SDK
 *      pool can only see sessions it created itself.
 *
 * Both are best-effort: a failure in either leaves the tokens cleared
 * locally and the user is signed out from this device immediately.
 * Sign-out should never block on network errors.
 */
export async function signOut(): Promise<void> {
  const tokens = loadTokens();
  const storage = storageInUse();
  clearTokens();
  if (!tokens) return;

  // /oauth2/revoke is open to public clients (no client_secret) when
  // EnableTokenRevocation is on the user-pool client (Cognito default).
  // Skip if cognitoDomain isn't configured (pre-Google-deploy stacks).
  const cfg = getConfig();
  const revoke =
    cfg.cognitoDomain && tokens.refreshToken
      ? fetch(`https://${cfg.cognitoDomain}/oauth2/revoke`, {
          method: "POST",
          headers: { "content-type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams({
            token: tokens.refreshToken,
            client_id: cfg.cognitoClientId,
          }),
        }).catch(() => undefined)
      : Promise.resolve();

  const user = pool(storage).getCurrentUser();
  const sdk: Promise<void> = user
    ? new Promise<void>((resolve) => {
        user.getSession((err: Error | null) => {
          if (err) return resolve();
          user.globalSignOut({
            onSuccess: () => resolve(),
            onFailure: () => resolve(),
          });
        });
      })
    : Promise.resolve();

  await Promise.all([revoke, sdk]);
}
